import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { includedIn } from '@fmnts/core/collection';
import {
  AbstractGraphNodeBuilder,
  DeclarativeFormComponent,
  GraphBuilderContext,
  OperatorNodeDefinition,
} from '@fmnts/declarative-forms';
import {
  distinctUntilChanged,
  filter,
  MonoTypeOperatorFunction,
  Observable,
  repeat,
  skip,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { useLatestAsDefaultValue } from '../rxjs/operators/use-latest-as-default-value.operator';
import {
  FieldGraphNodeBuilder,
  FieldReference,
  FormFieldSource,
} from './field-node-builder.service';

enum ControlUiState {
  Dirty = 'dirty',
  Pristine = 'pristine',
  Touched = 'touched',
  Untouched = 'untouched',
}

interface SetUiStateOptions {
  dirty?: boolean;
  pristine?: boolean;
  touched?: boolean;
  untouched?: boolean;
}

interface TakeUntilUiState {
  state: `${ControlUiState}`;
}

/**
 * Type guard for Control UI states
 */
const isControlUiState = includedIn<string, ControlUiState>(
  Object.values(ControlUiState),
);

export enum FormNodeName {
  /**
   * Emits with the current value of a referenced field
   */
  FieldValue = 'field.value$',
  /**
   * Emits changes to the value of a referenced field
   */
  FieldValueChanges = 'field.value_changes',
  /**
   * Emits with the current value of the field itself
   */
  FieldSelfValue = 'field.self.value$',
  /**
   * Emits with the current value of the whole form
   */
  FormValue = 'form.value$',
}

export enum FormOperatorName {
  EmitLatestOnReset = 'form.reset.emit-latest',
  /**
   * Filters emissions if a controls UI state doesn't match
   * the required status.
   *
   * Arguments:
   * - ui state: Required, must be one of the ui states
   * - field reference: Optional
   */
  FieldFilterUiState = 'field.filter.ui-state',
  /**
   * Emits with the latest value of the source observable when
   * - the source observable emits
   * - the form was reset
   */
  FieldRestoreAsDefault = 'field.default-value.restore',
  /**
   * Emits with the latest value of the source observable whenever
   * the source observable emits, or if the fields value was
   * changed
   */
  FieldUseLatestAsDefaultValue = 'field.default-value.use-latest',
  /** Resubscribes the source stream when the form is resetted. */
  RepeatOnReset = 'form.repeat-on-reset',
  SetUiState = 'field.set-ui-state',
  /** Unsubscribes from source stream when the control has a certain UI state.  */
  TakeUntilUiState = 'field.take-until-ui-state',
  /** Unsubscribes from source stream when the control becomes dirty. */
  TakeWhilePristine = 'field.take-while-pristine',
}

/**
 * Builder for form related graph nodes
 */
@Injectable({
  providedIn: 'root',
})
export class FormGraphNodeBuilder extends AbstractGraphNodeBuilder<
  FormNodeName,
  FormOperatorName
> {
  constructor(private common: FieldGraphNodeBuilder) {
    super();

    // Add sources
    this.addNode(FormNodeName.FieldValue, (def: FormFieldSource, ctx) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.fieldValue$(def.options!, ctx),
    )
      .addNode(FormNodeName.FieldValueChanges, (def: FormFieldSource, ctx) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.fieldValueChanges$(def.options!, ctx),
      )
      .addNode(FormNodeName.FieldSelfValue, (_, ctx) =>
        this.fieldSelfValue$(ctx),
      );
    this.addNode(FormNodeName.FormValue, (_, ctx) => this.formValue$(ctx));

    // Add operators
    this.addOperator(
      FormOperatorName.EmitLatestOnReset,
      (def: OperatorNodeDefinition<[]>, ctx) => this.emitLatestOnReset(ctx),
    );
    this.addOperator(
      FormOperatorName.FieldFilterUiState,
      (def: OperatorNodeDefinition<[string, FieldReference]>, ctx) =>
        this.filterControlUiState(def.args, ctx),
    );
    this.addOperator(
      FormOperatorName.FieldRestoreAsDefault,
      (def: OperatorNodeDefinition, ctx) => this.restoreAsDefault(ctx),
    );
    this.addOperator(FormOperatorName.FieldUseLatestAsDefaultValue, (_, ctx) =>
      this.fieldLatestUseAsDefaultValue(ctx),
    );
    this.addOperator(
      FormOperatorName.RepeatOnReset,
      (def: OperatorNodeDefinition<[]>, ctx) => this.repeatOnReset(ctx),
    );
    this.addOperator(
      FormOperatorName.SetUiState,
      (def: OperatorNodeDefinition<[SetUiStateOptions]>, ctx) =>
        this.setUiState(def.args?.[0] ?? {}, ctx),
    );
    this.addOperator(
      FormOperatorName.TakeUntilUiState,
      (def: OperatorNodeDefinition<[TakeUntilUiState]>, ctx) =>
        this.takeUntilUiState(def.args?.[0] ?? { state: 'dirty' }, ctx),
    );
    this.addOperator(
      FormOperatorName.TakeWhilePristine,
      (def: OperatorNodeDefinition<[]>, ctx) => this.takeWhilePristine(ctx),
    );
  }

  /**
   * @returns
   * An observable that emits with the value of the form.
   */
  private formValue$(ctx: GraphBuilderContext) {
    const form = this.getForm(ctx);
    if (!form) {
      throw new Error('Form not available in context');
    }

    return form.valueChanges;
  }

  /**
   * @param ref Reference
   * @param ctx Context
   *
   * @returns
   * An observable that is listening to a specific `fieldKey` for a component belonging to a form.
   */
  public fieldValue$<T = unknown>(
    ref: FieldReference,
    ctx: GraphBuilderContext,
  ): Observable<T> {
    const control = this.resolveFieldControl<T>(ref, ctx);

    return control.valueChanges.pipe(
      distinctUntilChanged(),
      startWith(control.value),
    );
  }

  /**
   * @param ref Reference
   * @param ctx Context
   *
   * @returns
   * A multicasting observable that emits an event every time the value of the control
   * refereced with `ref` changes.
   *
   * **NOTE**
   * This will not emit with the initial value, but instead only emit the actual changes.
   * If the initial value should also be emitted you can use `fieldValue$`
   */
  public fieldValueChanges$(
    ref: FieldReference,
    ctx: GraphBuilderContext,
  ): Observable<unknown> {
    const control = this.resolveFieldControl(ref, ctx);

    return control.valueChanges.pipe(
      startWith(control.value),
      distinctUntilChanged(),
      skip(1),
    );
  }

  /**
   * @param ctx Context
   *
   * @returns
   * An observable that emits with the value of the calling field itself.
   */
  public fieldSelfValue$(ctx: GraphBuilderContext): Observable<unknown> {
    return this.resolveFieldControl({ self: true }, ctx).valueChanges;
  }

  /**
   * In addition to emissions of the source, it will also emit the last
   * value of the source when form is resetted.
   *
   * @param ctx
   * @returns
   */
  private emitLatestOnReset<T>(
    ctx: GraphBuilderContext,
  ): MonoTypeOperatorFunction<T> {
    return (obs$) =>
      this.formReset(ctx).pipe(
        startWith(true),
        switchMap(() => obs$),
      );
  }

  private fieldLatestUseAsDefaultValue(ctx: GraphBuilderContext) {
    const control = this.resolveFieldControl({ self: true }, ctx);

    return useLatestAsDefaultValue(control);
  }

  /**
   * Filters emissions in case that a referenced control doesn't
   * have a desired ui state (dirty, pristine, touched, untouched).
   *
   * @param def Operator definition
   * @param ctx Graph context
   */
  private filterControlUiState<T>(
    def: [string, FieldReference] | undefined,
    ctx: GraphBuilderContext,
  ): MonoTypeOperatorFunction<T> {
    const [uiState, fieldReference = { self: true }] = def ?? [];

    if (!uiState || !isControlUiState(uiState)) {
      throw new Error(`
        arg[0] is ${uiState ?? 'empty'}
        should be one of: ${Object.values(ControlUiState).join(',')}.`);
    }

    const control = this.resolveFieldControl(fieldReference, ctx);

    return filter(() => control[uiState]);
  }

  /**
   * Resubscribes when form is resetted.
   * @param ctx
   * @returns
   */
  private repeatOnReset<T>(ctx: GraphBuilderContext) {
    return repeat<T>({
      delay: () => this.formReset(ctx),
    });
  }

  /**
   * Takes a source `Observable` which will be re-subscribed to whenever
   * the form is resetted. It will also initially subscribe and
   * mark the related control as pristine when
   * - it is the first submission
   * - control is untouched
   * - emitted value is the same as the current control value
   *
   * This is to ensure the correct pristine state for any control
   * which value is dynamically controlled via a graph node.
   *
   * @param ctx Graph context
   */
  private restoreAsDefault<T>(
    ctx: GraphBuilderContext,
  ): MonoTypeOperatorFunction<T> {
    const control = this.resolveFieldControl({ self: true }, ctx);
    const formReset$ = this.formReset(ctx);

    // Workaround to restore the pristine state after a page reload
    // This is needed because the field will be marked as dirty by
    // the form component, because the "default" value could be different
    // then the latest value with which the control was patched
    const restorePristineState = tap<T>((value) => {
      if (control.untouched && value === control.value) {
        control.markAsPristine();
      }
    });

    return (obs$) =>
      formReset$.pipe(
        startWith(true),
        switchMap((_, index) =>
          // We only need to worry about the initial emission when
          // restoring the controls pristine state
          index > 0 ? obs$ : obs$.pipe(restorePristineState),
        ),
      );
  }

  /**
   * Adds a side effect to the source observable that sets the UI state
   * of the control for which the graph is initialized.
   *
   * @param opts Options that specify which states should be set
   * @param ctx Context
   */
  private setUiState<T>(
    opts: SetUiStateOptions,
    ctx: GraphBuilderContext,
  ): MonoTypeOperatorFunction<T> {
    const control = this.resolveFieldControl({ self: true }, ctx);

    const noop = () => {};
    const setDirtyOrPristine = opts.dirty
      ? () => control.markAsDirty()
      : opts.pristine
        ? () => control.markAsPristine()
        : noop;

    const setTouchedOrUntouched = opts.touched
      ? () => control.markAsTouched()
      : opts.untouched
        ? () => control.markAsUntouched()
        : noop;

    return tap(() => {
      setDirtyOrPristine();
      setTouchedOrUntouched();
    });
  }

  /**
   * Unsubscribes when control is in a certain UI state.
   * @param opts
   * @param ctx
   * @returns
   */
  private takeUntilUiState<T>(
    opts: TakeUntilUiState,
    ctx: GraphBuilderContext,
  ): MonoTypeOperatorFunction<T> {
    const ref: FieldReference = { self: true };
    const control = this.resolveFieldControl<T>(ref, ctx);

    return takeUntil(
      control.valueChanges.pipe(
        startWith(control.value),
        this.filterControlUiState([opts.state, ref], ctx),
      ),
    );
  }

  /**
   * Emits values as long as the control is pristine (including form resets).
   */
  private takeWhilePristine<T>(
    ctx: GraphBuilderContext,
  ): MonoTypeOperatorFunction<T> {
    return (obs$) =>
      obs$.pipe(
        this.emitLatestOnReset(ctx),
        this.takeUntilUiState({ state: 'dirty' }, ctx),
        this.repeatOnReset(ctx),
      );
  }

  /**
   * Creates an `Observable` that emits when the form was resetted.
   *
   * @param ctx Context
   * @returns
   * An `Observable` that emits when the form was resetted.
   */
  private formReset(ctx: GraphBuilderContext): Observable<void> {
    return ctx.injector.get(DeclarativeFormComponent).formReset.asObservable();
  }

  /**
   * @param ref Field reference
   * @param ctx Context
   *
   * @returns
   * Resolves the control for the referenced field
   */
  private resolveFieldControl<TValue>(
    ref: FieldReference,
    ctx: GraphBuilderContext,
  ) {
    if (!ref) {
      throw new Error('Could not resolve FieldControl, missing FieldReference');
    }
    const path = this.common.pathByFieldReference(ref, ctx);
    const control = this.getForm(ctx)?.get(path) as AbstractControl<TValue>;

    if (!control) {
      throw new Error(
        `Could not find control with reference ${path.join('.')}`,
      );
    }

    return control;
  }

  private getForm(ctx: GraphBuilderContext) {
    const declarativeFormComponent = ctx.injector.get(
      DeclarativeFormComponent,
      null,
    );
    return declarativeFormComponent?.form;
  }
}
